// ═══════════════════════════════════════════════════════════════════════
// Temps › Tableau de bord (DRH, RH) — « peu de cartes », exceptions d'abord (conception § 14.5) :
//   1. exceptions nommées (3 à 5), 2. présence du jour, 3. absentéisme du mois et écart,
//   4. heures supplémentaires (contingent seulement s'il est paramétré), 5. clôture par site,
//   6. export du mois. Tout est lu dans les journées stockées (GET /api/temps/rh/tableau-de-bord).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Users, Activity, Timer, Lock, FileSpreadsheet, Inbox, TrendingUp, TrendingDown, Minus, CheckCircle2 } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette, Selecteur } from '../../design/composants';
import { NavTemps, useDroitsTemps, AccesTemps, lireRh, messageErreur, moisLong, heures, pourcent, dateHeure, aujourdhui, STATUTS_PERIODE, STATUTS_EXPORT } from './rhCommun';

const TONS = { danger: 'var(--sg-danger)', alerte: 'var(--sg-alerte)', neutre: 'var(--sg-texte-3)' };

/** Lien d'une exception (écran du menu Temps). */
function versException(lien) {
  if (!lien) return null;
  if (lien.ecran === 'journees') return `/temps/rh/journees?filtre=${lien.filtre || 'anomalies'}${lien.site ? `&site=${lien.site}` : ''}`;
  return { a_traiter: '/temps/rh/a-traiter', qualite: '/temps/rh/qualite' }[lien.ecran] || null;
}

const Chiffre = ({ valeur, unite, sous }) => (
  <div>
    <span style={{ fontSize: 28, fontWeight: 600, color: 'var(--sg-texte)', letterSpacing: '-0.02em' }}>{valeur}</span>
    {unite && <span style={{ fontSize: 13, color: 'var(--sg-texte-3)', marginLeft: 6 }}>{unite}</span>}
    {sous && <div className="rh-form-aide" style={{ marginTop: 2 }}>{sous}</div>}
  </div>
);

function Ecart({ valeur, unite, inverse = false }) {
  if (valeur === null || valeur === undefined) return <span className="rh-form-aide">Pas de comparaison possible</span>;
  const Icone = valeur > 0 ? TrendingUp : valeur < 0 ? TrendingDown : Minus;
  const mauvais = inverse ? valeur < 0 : valeur > 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, color: valeur === 0 ? 'var(--sg-texte-3)' : mauvais ? 'var(--sg-danger)' : 'var(--sg-succes)' }}>
      <Icone size={14} aria-hidden="true" />{valeur > 0 ? '+' : ''}{unite === 'points' ? `${String(valeur).replace('.', ',')} point${Math.abs(valeur) > 1 ? 's' : ''}` : heures(valeur)} par rapport au mois précédent
    </span>
  );
}

const LIBELLES_PRESENCE = [
  ['presents', 'présents'], ['missions', 'en mission'], ['formations', 'en formation'], ['conges', 'en congé'], ['absences_declarees', 'absence déclarée'],
  ['autres_absences', 'autres absences'], ['a_regulariser', 'à régulariser'], ['attendus', 'attendus, pas encore arrivés'], ['repos', 'repos ou férié'], ['non_calcules', 'journée pas encore calculée', 'journées pas encore calculées'],
];

export default function RhTempsTableauDeBordPage() {
  const droits = useDroitsTemps();
  const navigate = useNavigate();
  const [mois, setMois] = useState(aujourdhui().slice(0, 7));
  const [site, setSite] = useState('');
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const charger = useCallback(async () => {
    setErreur(''); setDonnees(null);
    try { setDonnees(await lireRh('/tableau-de-bord', { mois, site: site || undefined })); } catch (e) { setErreur(messageErreur(e, 'Chargement impossible.')); }
  }, [mois, site]);
  useEffect(() => { if (droits?.rh?.menu?.tableau_de_bord) charger(); }, [droits, charger]);
  const carte = (code) => donnees?.cartes.find((c) => c.code === code)?.donnees;
  const exceptions = carte('EXCEPTIONS'); const presence = carte('PRESENCE'); const abs = carte('ABSENTEISME');
  const hs = carte('HEURES_SUP'); const cloture = carte('CLOTURE'); const exp = carte('EXPORT');
  const t1 = hs?.tranches?.debut_t1_min; const t2 = hs?.tranches?.debut_t2_min;

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'Tableau de bord' }]} titre="Tableau de bord du temps"
          sousTitre="Les exceptions d'abord. Les chiffres viennent des journées déjà calculées."
          actions={droits?.rh?.menu?.a_traiter && <Bouton variante="primaire" Icone={Inbox} onClick={() => navigate('/temps/rh/a-traiter')}>À traiter</Bouton>} />
        <NavTemps actif="tableau_de_bord" />
        <AccesTemps droits={droits} rubrique="tableau_de_bord">
          <div className="rh-outils" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <input type="month" className="rh-champ-saisie" style={{ width: 'auto' }} aria-label="Mois" value={mois} max={aujourdhui().slice(0, 7)} onChange={(e) => e.target.value && setMois(e.target.value)} />
            {droits?.rh?.sites?.length > 1 && (
              <div style={{ minWidth: 220 }}><Selecteur libelle="Site" valeur={site} onChange={setSite} options={droits.rh.sites.map((s) => ({ valeur: s.id, libelle: s.nom }))} /></div>
            )}
          </div>
          {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
          {!donnees && !erreur && <Squelette largeur="60%" />}
          {donnees && (
            <>
              {/* 1. Exceptions nommées */}
              <Carte titre="À regarder en premier" Icone={AlertTriangle}>
                {!exceptions.length ? <EtatVide Icone={CheckCircle2} titre="Rien d'urgent" texte="Aucune anomalie ni décision en attente dans votre périmètre." /> : (
                  <ul className="rh-liste">
                    {exceptions.map((x) => {
                      const vers = versException(x.lien);
                      return (
                        <li key={`${x.code}-${x.site_id || ''}`} style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <AlertTriangle size={15} aria-hidden="true" style={{ color: TONS[x.ton] || TONS.neutre, flexShrink: 0 }} />{x.texte}
                          </span>
                          {vers && <Link to={vers} className="rh-btn rh-btn-petit rh-btn-discret" style={{ textDecoration: 'none' }}>Voir<ArrowRight size={14} aria-hidden="true" /></Link>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Carte>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                {/* 2. Présence du jour */}
                <Carte titre="Présence aujourd'hui" Icone={Users}>
                  <Chiffre valeur={pourcent(presence.taux)} sous={`${presence.compteurs.presents + presence.compteurs.missions + presence.compteurs.formations} au travail sur ${presence.attendus_aujourdhui} attendus (effectif : ${presence.effectif})`} />
                  <ul className="rh-liste" style={{ marginTop: 8 }}>
                    {LIBELLES_PRESENCE.filter(([k]) => presence.compteurs[k]).map(([k, l, pl]) => <li key={k}>{presence.compteurs[k]} {pl && presence.compteurs[k] > 1 ? pl : l}</li>)}
                  </ul>
                  <p className="rh-form-aide" style={{ margin: '6px 0 0' }}>Chaque personne compte dans une seule case : les cases s'additionnent à l'effectif. Le repos n'est pas une absence.</p>
                </Carte>

                {/* 3. Absentéisme */}
                <Carte titre={`Absentéisme — ${moisLong(abs.mois)}`} Icone={Activity}>
                  <Chiffre valeur={pourcent(abs.taux)} sous={abs.en_cours ? 'Mois en cours, jusqu\'à aujourd\'hui' : undefined} />
                  <div style={{ marginTop: 6 }}><Ecart valeur={abs.ecart_points} unite="points" /></div>
                  <p className="rh-form-aide" style={{ margin: '8px 0 0' }}>
                    Temps d'absence ({heures(abs.minutes_absence)}) rapporté au temps prévu ({heures(abs.minutes_prevues)}). Compte la maladie, les absences non justifiées,
                    les permissions et autres absences autorisées ; pas les congés, repos, fériés, missions ni formations.
                  </p>
                </Carte>

                {/* 4. Heures supplémentaires */}
                <Carte titre={`Heures sup. — ${moisLong(hs.mois)}`} Icone={Timer}>
                  <Chiffre valeur={heures(hs.total_min)} unite="validées" sous={`${hs.nb_salaries} salarié${hs.nb_salaries > 1 ? 's' : ''}`} />
                  <div style={{ marginTop: 6 }}><Ecart valeur={hs.ecart_min} unite="heures" /></div>
                  <ul className="rh-liste" style={{ marginTop: 8 }}>
                    <li>Heures sup. 1re tranche{t1 ? ` (au-delà de ${heures(t1)} par semaine)` : ''} : {heures(hs.t1_min)}</li>
                    <li>Heures sup. 2e tranche{t2 ? ` (au-delà de ${heures(t2)} par semaine)` : ''} : {heures(hs.t2_min)}</li>
                    <li>Payées : {heures(hs.payees_min)} · récupérées : {heures(hs.recuperees_min)}</li>
                  </ul>
                  {hs.contingent && (
                    <p style={{ margin: '8px 0 0', fontSize: 13 }}>
                      Contingent annuel ({heures(hs.contingent.plafond_min)} par salarié) : {hs.contingent.nb_salaries_alerte
                        ? <Badge ton="alerte">{hs.contingent.nb_salaries_alerte} salarié{hs.contingent.nb_salaries_alerte > 1 ? 's' : ''} à {Math.round(hs.contingent.seuil_alerte * 100)} % ou plus</Badge>
                        : 'aucun salarié proche du plafond'}
                    </p>
                  )}
                  <p className="rh-form-aide" style={{ margin: '6px 0 0' }}>Semaines rattachées au mois (celui qui contient leur dimanche).</p>
                </Carte>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
                {/* 5. Clôture par site */}
                <Carte titre={`Clôture — ${moisLong(cloture.mois)}`} Icone={Lock}
                  action={droits.rh.menu.cloture && <Link to={`/temps/rh/cloture?mois=${cloture.mois}`} className="rh-btn rh-btn-petit rh-btn-discret" style={{ textDecoration: 'none' }}>Clôture et paie<ArrowRight size={14} aria-hidden="true" /></Link>}>
                  {!cloture.sites.length ? <EtatVide titre="Aucun site" /> : (
                    <ul className="rh-liste">
                      {cloture.sites.map((s) => {
                        const [ton, libelle] = STATUTS_PERIODE[s.statut] || ['neutre', s.statut];
                        return (
                          <li key={s.site_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <span>{s.site_nom}</span>
                            <span><Badge ton={ton} point>{libelle}</Badge>{s.cloture_le && <span className="rh-cellule-secondaire"> le {dateHeure(s.cloture_le)}</span>}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Carte>
                {/* 6. Export du mois */}
                <Carte titre={`Export vers la paie — ${moisLong(exp.mois)}`} Icone={FileSpreadsheet}>
                  {!exp.sites.length ? <EtatVide titre="Aucun site" /> : (
                    <ul className="rh-liste">
                      {exp.sites.map((s) => {
                        const [ton, libelle] = s.statut ? STATUTS_EXPORT[s.statut] || ['neutre', s.statut] : ['neutre', 'Pas encore produit'];
                        return (
                          <li key={s.site_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                            <span>{s.site_nom}</span>
                            <span><Badge ton={ton}>{libelle}{s.version ? ` · version ${s.version}` : ''}</Badge></span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <p className="rh-form-aide" style={{ margin: '6px 0 0' }}>L'export est produit à la clôture ; seules des données clôturées partent en paie.</p>
                </Carte>
              </div>
            </>
          )}
        </AccesTemps>
      </div>
    </Layout>
  );
}
